import axios from "axios";
import React, {useState, useEffect, useRef} from "react";
import {isAndroid, isIOS} from "react-device-detect";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {imagesBlobState, secondaryModals, selectedShippingAddress, imageFrameState, giftCheckoutState} from "@atoms";
import {
  netPriceState,
  imageCountState,
  framePricesState,
  defaultFramePriceState,
  handlingFeeState,
  shippingPriceState,
  creditsAppliedState
} from "@atoms/priceCalc";
import "./style.css";
import {checkOrderStatus} from "../../../api/requests";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import AmountCharged from "../../../helpers/AmountCharged";
import {rememberOrderAccess} from "../../../helpers/utils/orderAccess";
import {useSecondModal} from "../../../helpers/hooks/useSecondModal";
import {validCoupon} from "../../../Recoil/Atoms";
import {useNavigate} from "react-router-dom";
import useGooglePay from "../../../helpers/hooks/useGooglePay";

// Frame key -> Hebrew label, for the itemized invoice line (pdesc).
const GPAY_FRAME_LABELS = { classic: "קלאסי", bold: "נועז", ever: "נקי", clean: "אותנטי", village: "כפרי", nordic: "נורדי" };

// Add a flag for testing mode
const TEST_MODE = process.env.NODE_ENV !== 'production';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const TRANZILA_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thfapplepay.js';

const SelectCardModalMobile = ({style}) => {
  const navigate = useNavigate();
  const scriptRef = useRef(null);
  const applePayButtonRef = useRef(null);
  const setModals = useSetRecoilState(secondaryModals);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const frameChoose = useRecoilValue(imageFrameState);
  const modal = useSecondModal();
  const [isBitLoading, setBitLoading] = useState(false);
  const [isApplePayLoading, setApplePayLoading] = useState(false);

  // Google Pay (Tranzila Hosted Fields). Shown ALONGSIDE Apple Pay here (payment
  // selection) so the user can pick either. Preloads order + handshake token so the
  // tap can call chargeGpay synchronously (Google Pay needs the user gesture).
  const { googlePayEligible, chargeGooglePay } = useGooglePay();
  const [gpayOrderId, setGpayOrderId] = useState(null);
  const [gpayThtk, setGpayThtk] = useState(null);

  const imageCount = useRecoilValue(imageCountState);
  const imagesBlob = useRecoilValue(imagesBlobState);
  const [numberOfImages] = useState(5);
  const netPrice = useRecoilValue(netPriceState);
  const selectedCoupon = useRecoilValue(validCoupon);
  const [framePrices] = useRecoilState(framePricesState);
  const [defaultFramePrice] = useRecoilState(defaultFramePriceState);
  const handlingFee = useRecoilValue(handlingFeeState);
  // Configured shipping (/api/shipping, loaded in App) — the same price the drawer charges.
  const shippingPrice = useRecoilValue(shippingPriceState);
  // Points the checkout drawer applied to this cart. The payment webhook spends
  // cart.creditsApplied after the charge, so the charge must already include them.
  const creditsApplied = useRecoilValue(creditsAppliedState);

  // Get the current frame type from user's choice or fallback to localStorage
  const currentFrameType = frameChoose || localStorage.getItem("frameSelected") || "classic";
  
  // Get the price for the current frame type, or fall back to the default price
  const framePrice = framePrices[currentFrameType] || defaultFramePrice;

  // GIFT-CARD mode: charge the server-priced gift amount against the GC- order
  // (pre-created — no checkorder / createorder here).
  const giftCheckout = useRecoilValue(giftCheckoutState);
  const giftMode = !!giftCheckout;

  const amountCharged = giftMode ? giftCheckout.amount : AmountCharged(imageCount + imagesBlob.length, numberOfImages,
    netPrice + (imagesBlob.length * framePrice), shippingPrice, selectedCoupon, handlingFee, creditsApplied);

  // The card form charges + records the order itself (one press) — no verify-first mode.
  const handleOpenNewCardForm = () => {
    setModals(md => ({
      ...md,
      selectCardMobile: {visible: false},
      addCardMobile: {visible: true},
    }));
  };

  const creatOrder = async (data) => {
    try {
      rememberOrderAccess(); // the success page needs these after localStorage.clear()
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const res = await axios.post(`${BASE_URL}/gama-createorder`, { ...data, sessionId: localStorage.getItem("sessionId") }, config);
      if (res.data.status === 200) {
        console.log("----success-----");
      }
      return res.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const [isApplePaySupported, setIsApplePaySupported] = useState(false);
  const [isApplePayScriptLoaded, setIsApplePayScriptLoaded] = useState(false);
  
  // Add a function to force Apple Pay to be visible for testing
  const forceShowApplePay = () => {
    console.log("SelectCardMobile: Forcing Apple Pay button to be visible for testing");
    setIsApplePaySupported(true);
  };
  
  // Expose the function to the window object in test mode
  if (TEST_MODE && typeof window !== 'undefined') {
    window.forceShowApplePay = forceShowApplePay;
  }

  // Function to load the Tranzila script
  const loadTranzilaScript = () => {
    console.log("SelectCardMobile: Loading Apple Pay script...");
    
    // Check if script is already loaded
    if (window.hfChargeApple) {
      console.log("SelectCardMobile: Apple Pay script already loaded");
      setIsApplePayScriptLoaded(true);
      return;
    }
    
    try {
      // Remove any existing scripts to avoid duplicates
      if (scriptRef.current) {
        try {
          document.body.removeChild(scriptRef.current);
        } catch (err) {
          console.error("SelectCardMobile: Error removing existing script:", err);
        }
      }
      
      // Create script element for jQuery
      const jqueryScript = document.createElement('script');
      jqueryScript.src = "https://code.jquery.com/jquery-3.6.0.min.js";
      jqueryScript.async = true;
      
      jqueryScript.onload = () => {
        console.log("SelectCardMobile: jQuery loaded successfully");
        
        // Create script element for Tranzila
        const script = document.createElement('script');
        script.src = `${TRANZILA_SCRIPT_URL}?v=${Date.now()}`;
        script.async = true;
        
        script.onload = () => {
          console.log("SelectCardMobile: Tranzila script loaded successfully");
          
          // Add noConflict after script loads
          const noConflictScript = document.createElement('script');
          noConflictScript.textContent = 'var $n = jQuery.noConflict(true);';
          document.body.appendChild(noConflictScript);
          
          setIsApplePayScriptLoaded(true);
          
          // Verify that the function exists
          if (typeof window.hfChargeApple !== 'function') {
            console.error("SelectCardMobile: Script loaded but hfChargeApple function not found");
            modal("open", "errorCart");
          } else {
            console.log("SelectCardMobile: hfChargeApple function found and ready to use");
          }
        };
        
        script.onerror = (error) => {
          console.error("SelectCardMobile: Tranzila script loading error:", error);
          modal("open", "errorCart");
        };
        
        document.body.appendChild(script);
        scriptRef.current = script;
      };
      
      jqueryScript.onerror = (error) => {
        console.error("SelectCardMobile: jQuery loading error:", error);
        modal("open", "errorCart");
      };
      
      document.body.appendChild(jqueryScript);
    } catch (err) {
      console.error("SelectCardMobile: Error in script setup:", err);
      modal("open", "errorCart");
    }
  };

  // Set up the global function for Apple Pay
  useEffect(() => {
    // This will be called by the HTML button's onclick
    window.setupApplePay = async (e) => {
      // Prevent default form submission if event is passed
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      
      console.log("SelectCardMobile: setupApplePay called");
      
      if (!isApplePayScriptLoaded || typeof window.hfChargeApple !== 'function') {
        console.error("SelectCardMobile: Apple Pay script not loaded or hfChargeApple function not found");
        modal("open", "errorCart");
        return;
      }
      
      setApplePayLoading(true);
      
      try {
        let haveToCreate = true;
        let orderId = localStorage.getItem("orderId");

        console.log("SelectCardMobile: Checking existing order:", orderId);

        if (giftMode) {
          haveToCreate = false; // GC- order already exists server-side
          orderId = giftCheckout.giftOrderId;
        } else if (orderId) {
          const checkResp = await checkOrderStatus();
          console.log("SelectCardMobile: Order status check response:", checkResp);

          if (checkResp.data && checkResp.data.orderStatus === OrderStatus.NOT_PAID) {
            console.log("SelectCardMobile: Using existing unpaid order");
            haveToCreate = false;
          } else {
            console.log("SelectCardMobile: Removing existing order as it's paid or failed");
            localStorage.removeItem("orderId");
            orderId = null;
            throw new Error("Order is already paid or failed");
          }
        }

        if (haveToCreate) {
          console.log("SelectCardMobile: Creating new order");
          // Get the current frame selection from user's choice or fallback to localStorage
          const currentFrameType = frameChoose || localStorage.getItem("frameSelected");
          
          const orderData = await creatOrder(
            {
              ...selectedAddress,
              uid: localStorage.getItem("uniqueUserId"),
              finalAmount: amountCharged,
              orderStatus: OrderStatus.NOT_PAID,
              imagesPayed: imageCount + imagesBlob.length,
              frameType: currentFrameType // Add the frame type to the order data
            });

          console.log("SelectCardMobile: Order creation response:", orderData);
          
          if (!orderData || !orderData.oid) {
            console.error("SelectCardMobile: Order creation failed or missing order ID");
            throw new Error("Creating order failed.");
          }

          orderId = orderData.oid;
          localStorage.setItem("orderId", orderId);
          console.log("SelectCardMobile: New order ID:", orderId);
        }

        // Get handshake token
        console.log("SelectCardMobile: Requesting Apple Pay handshake token");
        const handshakeResponse = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amountCharged}`);
        console.log("SelectCardMobile: Handshake response:", handshakeResponse.data);
        
        if (handshakeResponse.data.status !== 200 || !handshakeResponse.data.token) {
          console.error("SelectCardMobile: Handshake failed or missing token:", handshakeResponse.data);
          throw new Error("Apple Pay handshake failed.");
        }

        const handshakeToken = handshakeResponse.data.token;
        
        console.log("SelectCardMobile: Calling hfChargeApple directly with token:", handshakeToken);
        
        // Call Apple Pay function directly
        window.hfChargeApple({
          // REQUIRED PARAMS
          labelApple: giftMode ? "גיפט קארד בלנדס" : "תמונות ממוסגרות", // product name (required)
          terminal_name: "blends", // terminal name (required)
          currency_code: "ILS", // currency code (required)
          amount: amountCharged, // transaction amount (required)
          tran_mode: "A", // transaction mode (required)
          thtk: handshakeToken, // handshake token (required)

          // OPTIONAL PARAMS
          response_language: 'english', // api error language
          supported_network: ['visa', 'masterCard', 'amex', 'discover'], // default values
          pdesc: giftMode ? "גיפט קארד בלנדס" : "תמונות ממוסגרות", // product description
          notify_url: giftMode
            ? `${window.location.origin}/api/apple-pay-webhook?orderId=${orderId}`
            : `https://webhook.site/c357eb9b-fe36-4376-9a68-f721f5972b46`, // notification URL
          debug: true, // Enable debug mode
        }, function (err, response) {
          console.log('SelectCardMobile: hfChargeApple callback triggered');
          setApplePayLoading(false);

          if (err) {
            console.error("SelectCardMobile: Error in hfChargeApple:", err);
            modal("open", "errorCart");
            return;
          }

          console.log('SelectCardMobile: hfChargeApple response:', response);

          if (response && response.status === "ok") {
            console.log("SelectCardMobile: Payment successful");

            if (giftMode) {
              // The webhook GC- branch activates the card; just route to success.
              localStorage.removeItem("orderId");
              navigate(`/gift-success/${orderId}`);
              return;
            }

            // Update the order status on the server
            axios.post(`${BASE_URL}/user/changeorderstatus/${orderId}`, {
              status: OrderStatus.PAID,
              amountCharged: amountCharged,
            }).then(response => {
              console.log("SelectCardMobile: Order status updated:", response.data);
            }).catch(error => {
              console.error("SelectCardMobile: Error updating order status:", error);
            });

            // Redirect to success page
            const oid = localStorage.getItem("orderId");
            localStorage.clear();
            navigate(`/payment-success/${oid}`);
          } else {
            console.error("SelectCardMobile: Payment response not ok:", response);
            modal("open", "errorCart");
          }
        });
      } catch (err) {
        console.error("SelectCardMobile: Error in setupApplePay:", err);
        modal("open", "errorCart");
        setApplePayLoading(false);
      }
    };
    
    return () => {
      // Clean up
      delete window.setupApplePay;
    };
  }, [amountCharged, creatOrder, navigate, imageCount, imagesBlob.length, isApplePayScriptLoaded, modal, selectedAddress]);

  // Add a safety timeout to automatically reset the loading state after a certain period
  useEffect(() => {
    let timeout;
    if (isApplePayLoading) {
      console.log("🍎 Apple Pay: Setting safety timeout to auto-reset loading state after 60 seconds");
      timeout = setTimeout(() => {
        console.log("🍎 Apple Pay: Safety timeout triggered - auto-resetting loading state");
        setApplePayLoading(false);
      }, 10000); // 10 seconds loading state timeout - Change this loading state to false when apple pay modal is opened.
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isApplePayLoading]);

  useEffect(() => {
    // Check if Apple Pay is supported in the browser
    if (window.ApplePaySession && isIOS) {
      setIsApplePaySupported(true);
      // Load the script if Apple Pay is supported
      loadTranzilaScript();
    }
    
    // In test mode, check for a URL parameter to force Apple Pay to be visible
    if (TEST_MODE && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('showApplePay') === 'true') {
        forceShowApplePay();
        // Load the script in test mode
        loadTranzilaScript();
      }
    }
    
    return () => {
      // Clean up script on unmount
      if (scriptRef.current) {
        try {
          document.body.removeChild(scriptRef.current);
          scriptRef.current = null;
        } catch (err) {
          console.error("SelectCardMobile: Error removing script on unmount:", err);
        }
      }
    };
  }, []);

  const handleBitPayment = async () => {
    setBitLoading(true);

    let haveToCreate = true;
    let gamaAuthResponse;
    let bitModalData;
    const gamaAccessToken = localStorage.getItem("gamaAccessToken");
    const expiresAt = localStorage.getItem("gamaAccessTokenExpiresAt");

    try {
      if (giftMode) {
        haveToCreate = false; // GC- order already exists server-side
      } else if (localStorage.getItem("orderId")) {
        const checkResp = await checkOrderStatus();
        if (checkResp.data && checkResp.data.orderStatus === OrderStatus.NOT_PAID) {
          haveToCreate = false;
        } else {
          localStorage.removeItem("orderId");
          throw new Error("Order is already paid or failed");
        }
      }

      if (!gamaAccessToken || expiresAt <= Date.now()) {
        gamaAuthResponse = await axios.post(`${BASE_URL}/gama-login`);

        if (gamaAuthResponse.data.status !== 200) {
          throw new Error("Gama auth failed.");
        }

        localStorage.setItem("gamaAccessToken", gamaAuthResponse.data.access_token);
        localStorage.setItem("gamaAccessTokenExpiresAt", (Date.now() + gamaAuthResponse.data.expires_in));
      }

      const gamaSessionResponse = await axios.post(`${BASE_URL}/gama-session`, {
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
      });

      if (gamaSessionResponse.data.status !== 200) {
        throw new Error("Gama session creating failed.");
      }

      if (haveToCreate) {
        // Get the current frame selection from user's choice or fallback to localStorage
        const currentFrameType = frameChoose || localStorage.getItem("frameSelected");
        
        const orderData = await creatOrder(
          {
            ...selectedAddress,
            uid: localStorage.getItem("uniqueUserId"),
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType // Add the frame type to the order data
          });

        if (!orderData) {
          throw new Error("Creating order failed.");
        }

        localStorage.setItem("orderId", orderData.oid);
      }

      const id = localStorage.getItem("orderId");

      const createTransactionResponse = await axios.post(`${BASE_URL}/gama-create-payment`, {
        orderId: id,
        amount: amountCharged,
        payerName: (giftMode ? giftCheckout.buyerName : selectedAddress.fullName) || "Blends",
        payerPhoneNumber: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
        transactionId: gamaSessionResponse.data.transactionProcessingId,
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
        gamaSessionToken: gamaSessionResponse.data.token,
        transactionEndpoint: gamaSessionResponse.data.transactionProcessingEndpoint,
      });

      if (createTransactionResponse.data.status !== 200) {
        throw new Error("Gama transaction creating failed.");
      }

      if (isAndroid) {
        bitModalData = {
          amountCharged,
          strLink: createTransactionResponse.data.walletProviderApplicationSchemeAndroid,
        };
        console.log("Android");
      } else if (isIOS) {
        bitModalData = {
          amountCharged,
          strLink: createTransactionResponse.data.walletProviderApplicationSchemeIos,
        };
        console.log("iOS");
      }

      setModals(state => ({
        ...state,
        selectCardMobile: {visible: false},
        bitPayMobile: {visible: true, data: bitModalData},
      }));
    } catch (err) {
      console.log(err);
      modal("open", "errorCart");
    } finally {
      setBitLoading(false);
    }
  };

  // Enhanced handleApplePayment function that gets token first, then opens modal
  const handleApplePayment = async (e) => {
    // Prevent any default behavior
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    console.log("🍎 Apple Pay: Starting payment process - preparing order and getting handshake token...");
    setApplePayLoading(true);
    
    try {
      // Check if script is loaded
      if (!isApplePayScriptLoaded) {
        console.log("🍎 Apple Pay: Script not loaded yet. Loading script now - please wait...");
        loadTranzilaScript();
        // Wait for script to load
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (!window.hfChargeApple) {
          throw new Error("Apple Pay script failed to load");
        }
      }
      
      // Handle order creation first
      let orderId = localStorage.getItem("orderId");
      let haveToCreate = true;

      console.log("🍎 Apple Pay: Checking if you have an existing order...", orderId ? `Found order: ${orderId}` : "No existing order found");

      if (giftMode) {
        haveToCreate = false; // GC- order already exists server-side
        orderId = giftCheckout.giftOrderId;
      } else if (orderId) {
        const checkResp = await checkOrderStatus();
        console.log("SelectCardMobile: Order status check response:", checkResp);
        
        if (checkResp.data && checkResp.data.orderStatus === OrderStatus.NOT_PAID) {
            console.log("🍎 Apple Pay: Using your existing unpaid order - no need to create a new one");
          haveToCreate = false;
        } else {
            console.log("🍎 Apple Pay: Your existing order is already paid or has failed. Creating a new order...");
          localStorage.removeItem("orderId");
          orderId = null;
        }
      }

      if (haveToCreate) {
        console.log("🍎 Apple Pay: Creating a new order for your purchase...");
        // Get the current frame selection from user's choice or fallback to localStorage
        const currentFrameType = frameChoose || localStorage.getItem("frameSelected");
        
        const orderData = await creatOrder(
          {
            ...selectedAddress,
            uid: localStorage.getItem("uniqueUserId"),
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType // Add the frame type to the order data
          });

        console.log("🍎 Apple Pay: Order created successfully!", orderData);
        
        if (!orderData || !orderData.oid) {
          console.error("SelectCardMobile: Order creation failed or missing order ID");
          throw new Error("Creating order failed.");
        }

        orderId = orderData.oid;
        localStorage.setItem("orderId", orderId);
        console.log("🍎 Apple Pay: Your new order ID is:", orderId);
      }

      // Get handshake token
      console.log("🍎 Apple Pay: Requesting secure handshake token from Tranzila...");
      const handshakeResponse = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amountCharged}`);
      console.log("🍎 Apple Pay: Received handshake response from Tranzila:", handshakeResponse.data);
      
      if (handshakeResponse.data.status !== 200 || !handshakeResponse.data.token) {
        console.error("SelectCardMobile: Handshake failed or missing token:", handshakeResponse.data);
        throw new Error("Apple Pay handshake failed.");
      }

      const handshakeToken = handshakeResponse.data.token;
      console.log("🍎 Apple Pay: Successfully received handshake token! Opening Apple Pay payment screen...");
      
      // Open the ApplePayMobile modal with the token and amount
      // Also pass an onClose callback to reset the loading state when the modal is closed
      setModals(state => ({
        ...state,
        selectCardMobile: {visible: false},
        applePayMobile: {
          visible: true, 
          data: {
            amount: amountCharged,
            orderId: orderId,
            handshakeToken: handshakeToken,
            onClose: () => {
              console.log("🍎 Apple Pay: Payment modal closed, resetting loading state");
              setApplePayLoading(false);
            }
          }
        },
      }));
      
    } catch (err) {
      console.error("SelectCardMobile: Error in handleApplePayment:", err);
      modal("open", "errorCart");
      setApplePayLoading(false);
    }
  };

  // Preload order + handshake token (thtk) for Google Pay once it's eligible, so the
  // tap fires chargeGpay synchronously (Google Pay requires the user gesture).
  useEffect(() => {
    const preload = async () => {
      if (!googlePayEligible || (!giftMode && !selectedAddress?.email) || gpayThtk) return;
      try {
        // Gift mode: the GC- order already exists server-side — just handshake.
        if (giftMode) {
          const hs = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${giftCheckout.amount}`);
          if (hs.data.status === 200 && hs.data.token) {
            setGpayOrderId(giftCheckout.giftOrderId);
            setGpayThtk(hs.data.token);
          }
          return;
        }
        let orderId = localStorage.getItem("orderId");
        if (orderId) {
          const checkResp = await checkOrderStatus();
          if (!(checkResp?.data && checkResp.data.orderStatus === OrderStatus.NOT_PAID)) {
            localStorage.removeItem("orderId");
            orderId = null;
          }
        }
        if (!orderId) {
          const orderData = await creatOrder({
            ...selectedAddress,
            uid: localStorage.getItem("uniqueUserId"),
            finalAmount: amountCharged,
            orderStatus: OrderStatus.NOT_PAID,
            imagesPayed: imageCount + imagesBlob.length,
            frameType: currentFrameType
          });
          if (!orderData?.oid) return;
          orderId = orderData.oid;
          localStorage.setItem("orderId", orderId);
        }
        const handshakeResponse = await axios.get(`${BASE_URL}/apple-pay-handshake?amount=${amountCharged}`);
        if (handshakeResponse.data.status === 200 && handshakeResponse.data.token) {
          setGpayOrderId(orderId);
          setGpayThtk(handshakeResponse.data.token);
        }
      } catch (e) {
        console.error("SelectCardMobile: Google Pay preload failed", e);
      }
    };
    preload();
  }, [googlePayEligible, selectedAddress, amountCharged, imageCount, imagesBlob.length, currentFrameType, giftMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Google Pay charge — synchronous from the tap; mirrors handleApplePayment's outcome.
  // Server-side trace (mobile has no console) — same endpoint Apple Pay debug uses.
  const gpaySelDebug = (step, data) => {
    axios.post(`${BASE_URL}/apple-pay-debug`, { step, data, timestamp: new Date().toISOString() }).catch(() => {});
  };

  const handleGooglePayment = () => {
    console.log("[GPay select] tap", { hasOrder: !!gpayOrderId, hasThtk: !!gpayThtk });
    gpaySelDebug("GPAY_SELECT_1_TAP", { hasOrder: !!gpayOrderId, hasThtk: !!gpayThtk });
    if (!gpayOrderId || !gpayThtk) {
      console.warn("SelectCardMobile: Google Pay not ready yet");
      gpaySelDebug("GPAY_SELECT_ERR_NOT_PRELOADED");
      return;
    }
    setApplePayLoading(true);
    gpaySelDebug("GPAY_SELECT_2_CHARGE_CALLED", { orderId: gpayOrderId, amount: amountCharged });
    const framesCount = giftMode ? giftCheckout.frames : imageCount + imagesBlob.length;
    const frameLabel = GPAY_FRAME_LABELS[currentFrameType] || GPAY_FRAME_LABELS.classic;
    const invoiceProductDesc = giftMode
      ? `גיפט קארד בלנדס ×${framesCount}`
      : `תמונות ממוסגרות – ${frameLabel} ×${framesCount}`;
    chargeGooglePay({
      terminal_name: "blends",
      response_language: 'english',
      currency_code: "ILS",
      amount: amountCharged,
      tran_mode: "A",
      thtk: gpayThtk,
      pdesc: invoiceProductDesc,
      contact: giftMode ? giftCheckout.buyerName : selectedAddress.fullName,
      email: (giftMode ? giftCheckout.buyerEmail : selectedAddress.email) || "",
      phone: giftMode ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
      address: giftMode ? "" : selectedAddress.address,
      city: giftMode ? "" : selectedAddress.city,
      uniqueid: gpayOrderId,
      notify_url: `${window.location.origin}/api/google-pay-webhook?orderId=${gpayOrderId}&method=google_pay`,
    }, (err, response) => {
      console.log("[GPay select] chargeGpay callback", { err, status: response?.status });
      gpaySelDebug("GPAY_SELECT_3_CALLBACK", { err: err?.message || err || null, status: response?.status });
      setApplePayLoading(false);
      if (err) {
        console.error("SelectCardMobile: Google Pay error", err);
        modal("open", "errorCart");
        return;
      }
      const isSuccess = response?.transaction_response?.success === true || response?.status === "ok";
      const last4Digits = response?.transaction_response?.credit_card_last_4_digits;
      if (isSuccess) {
        if (giftMode) {
          // The webhook GC- branch activates the card; just route to success.
          localStorage.removeItem("orderId");
          navigate(`/gift-success/${gpayOrderId}`);
          return;
        }
        axios.post(`${BASE_URL}/user/changeorderstatus/${gpayOrderId}`, {
          status: OrderStatus.PAID,
          amountCharged: amountCharged,
          last4Digits: last4Digits,
          paymentMethod: 'google_pay'
        }).catch(e => console.error("Error updating order status:", e));
        const oid = gpayOrderId;
        localStorage.clear();
        navigate(`/payment-success/${oid}`);
      } else {
        modal("open", "errorCart");
      }
    });
  };

  return (
    <div className="add-card-modal-mobile" style={{...style}}>
      <button 
        onClick={() => handleOpenNewCardForm()} 
        style={{color: "black", fontWeight: "500"}}
        disabled={isBitLoading || isApplePayLoading}
      >
        כרטיס אשראי
        <img src="/assets/images/card-box-pos.svg" alt="add-new-card" />
      </button>
      <ul>
        <button className="bitpayment" onClick={() => handleBitPayment()} disabled={isBitLoading || isApplePayLoading}>
          תשלום בביט
          {isBitLoading && <span className="buttonspinner"></span>}
          <img src="/assets/images/bitcartdark.svg" alt="bitpayment" />
        </button>
      </ul>
      
      {/* HTML Form for Apple Pay as per documentation */}
      <form id="apple-pay-form" style={{margin: 0, padding: 0}} onSubmit={(e) => e.preventDefault()}>
        {/* Hidden HTML button for Apple Pay as per documentation */}
        <button 
          type="button" // Explicitly set type to button to prevent form submission
          ref={applePayButtonRef}
          className="pay_button" 
          id="payApple" 
          style={{display: "none"}}
          onClick={(e) => window.setupApplePay(e)}
        >
          Pay with<img src="/assets/file/images/apple-icon.svg" alt="Apple Pay"/>
        </button>
      </form>
      
      {/* Visible styled button for Apple Pay */}
      {isApplePaySupported && (
        <ul>
          <button className="applepayment" onClick={handleApplePayment} disabled={isApplePayLoading || isBitLoading}>
            <p>שלמו עם Apple Pay</p>
            {isApplePayLoading && <span className="buttonspinner"></span>}
            <img src="/assets/file/images/applepay-selector.svg" alt="applepayment" />
          </button>
        </ul>
      )}

      {/* Google Pay — shown alongside Apple Pay (both offered here). Enabled once
          Tranzila fires `googleIsEnable` and the order + handshake are preloaded.
          ANDROID GATED OFF: Tranzila's SDK can't open the sheet on Android Chrome
          (no capability delegation on their postMessage relay; errors swallowed).
          Remove !isAndroid once Tranzila fixes their side. */}
      {googlePayEligible && !isAndroid && (
        <ul>
          <button className="applepayment gpayment" onClick={handleGooglePayment} disabled={isApplePayLoading || isBitLoading || !gpayThtk}>
            <p>שלמו באמצעות Google Pay</p>
            {isApplePayLoading && <span className="buttonspinner"></span>}
            <img src="https://www.gstatic.com/instantbuy/svg/dark_gpay.svg" alt="Google Pay" />
          </button>
        </ul>
      )}
      {/* <ul>
        <button onClick={() => handleOpenNewCardForm(false)} style={{color: "black", fontWeight: "500"}}>
        כרטיס דביט\דיירקט
          <img src="/assets/images/credit-pos.svg" alt="add-new-card" />
        </button>
      </ul> */}
    </div>
  );
};

export default SelectCardModalMobile;
